import { create } from 'zustand';
import type { User } from '@vdpm/shared';
import { apiClient } from '../lib/apiClient';

/**
 * Auth state. Replaces the old persisted Zustand store entirely.
 *
 * The session itself lives in an HttpOnly cookie set by the server. This store
 * only mirrors the user record so React components can read it synchronously.
 *
 * Importantly: nothing about the user is persisted to localStorage. Reload the
 * page and we re-fetch /api/auth/me, which validates the session server-side.
 */

export type LoginStep = 'idle' | 'verify_required' | 'enroll_required' | 'authenticated';

interface HydrateOpts {
  /**
   * When true, does not flip `loading` — avoids unmounting the login page.
   * Without this, `login()` → `hydrate()` briefly sets loading=true, `App`
   * replaces the whole tree with FullScreenLoader, and Login loses local state
   * (QR enrollment step, form fields).
   */
  silent?: boolean;
}

interface AuthState {
  user: User | null;
  twoFactorPending: boolean;
  loading: boolean;
  step: LoginStep;
  hydrate: (opts?: HydrateOpts) => Promise<void>;
  login: (email: string, password: string) => Promise<{ next: LoginStep; mustChangePass: boolean }>;
  verifyTotp: (token: string) => Promise<{ recoveryCodes?: string[] }>;
  enroll: () => Promise<{ qrDataUrl: string; manualKey: string }>;
  recover: (code: string) => Promise<void>;
  logout: () => Promise<void>;
}

interface MeResponse {
  user: User | null;
  twoFactorPending?: boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  twoFactorPending: false,
  loading: true,
  step: 'idle',

  hydrate: async (opts?: HydrateOpts) => {
    const silent = !!opts?.silent;
    if (!silent) set({ loading: true });
    try {
      const me = await apiClient.get<MeResponse>('/auth/me');
      set({
        user: me.user,
        twoFactorPending: !!me.twoFactorPending,
        step: !me.user
          ? 'idle'
          : me.twoFactorPending
            ? me.user.totpEnrolled
              ? 'verify_required'
              : 'enroll_required'
            : 'authenticated',
        ...(!silent ? { loading: false } : {}),
      });
    } catch {
      set({ user: null, twoFactorPending: false, step: 'idle', ...(!silent ? { loading: false } : {}) });
    }
  },

  login: async (email, password) => {
    const r = await apiClient.post<{
      ok: boolean;
      twoFactor: 'verify_required' | 'enroll_required';
      mustChangePass: boolean;
    }>('/auth/login', { email, password });
    await get().hydrate({ silent: true });
    return { next: r.twoFactor, mustChangePass: r.mustChangePass };
  },

  verifyTotp: async (token) => {
    const r = await apiClient.post<{ ok: boolean; recoveryCodes?: string[] }>(
      '/auth/2fa/verify',
      { token },
    );
    await get().hydrate({ silent: true });
    return { recoveryCodes: r.recoveryCodes };
  },

  enroll: async () => {
    return apiClient.post<{ qrDataUrl: string; manualKey: string }>('/auth/2fa/enroll');
  },

  recover: async (code) => {
    await apiClient.post('/auth/2fa/recover', { code });
    await get().hydrate({ silent: true });
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      set({ user: null, twoFactorPending: false, step: 'idle' });
    }
  },
}));
